import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Collapsible from '@radix-ui/react-collapsible';
import { DatePicker as CustomDatePicker } from './dateTimePickers';
import { HolidayConfigInput, HolidayConfigView, HolidayInput } from '../models/general';
import * as HolidaysApi from '../api/General/holidays';
import { isNullOrWhiteSpace, shadeBlend } from '../utils';
import { formatLocalDateParam } from '../utils/apiDate';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from './ui/Table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from './ui/Dialog';
import Input from './ui/Input';
import Button from './ui/Button';
import { Checkbox } from './ui/Checkbox';
import { Spinner } from './ui/Spinner';
import AppIcon from './AppIcon';
import { useToast, ToastRenderer } from '../hooks/useToast';

const HolidaysConfig: React.FC = () => {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const company = useSelector((state: any) => state.user.company);
  const companyPrimaryColor = useSelector((state: any) => state.user.companyPrimaryColor);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<HolidayConfigView[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [createHolidayDialogOpen, setCreateHolidayDialogOpen] = useState(false);
  const [holidayInputError, setHolidayInputError] = useState<string | undefined>();
  const [holidaysInput, setHolidaysInput] = useState<(HolidayInput & { dateParsed: Date })[]>([
    { description: '', name: '', defaultDate: formatLocalDateParam(new Date()), dateParsed: new Date(), id: 0 },
  ]);

  const tableDataGenerator = useCallback((countriesCount: number, holidayConfigInputList: HolidayConfigView[]) => {
    const body: HolidayConfigView[] = [];
    const grp: any[] = [];
    let currentHoliday = 0;
    holidayConfigInputList.forEach((hc) => {
      if (currentHoliday !== hc.holidayId) {
        currentHoliday = hc.holidayId;
        grp.push({
          key: hc.holidayId.toString(),
          name: hc.holiday.name,
          startIndex: body.length,
          count: countriesCount,
          level: 0,
          isCollapsed: true,
        });
      }
      body.push({ ...hc });
    });
    setItems(body);
    setGroups(grp);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { payload } = await HolidaysApi.getHolidaysConfig(company?.id);
      tableDataGenerator(payload.countriesCount, payload.holidayConfigInputList);
    } catch (e) {
      showToast(t('Failed to load holidays'), 'error');
    } finally {
      setLoading(false);
    }
  }, [company?.id, tableDataGenerator, t, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const itemsToUpdate: HolidayConfigInput[] = [];
      items.forEach((item) => {
        if (item && (item.id > 0 || item.enabled)) {
          const dateObj = item.date ? new Date(item.date) : undefined;
          if (dateObj) {
            dateObj.setHours(dateObj.getHours() - dateObj.getTimezoneOffset() / 60);
          }
          itemsToUpdate.push({
            id: item.id,
            enabled: item.enabled,
            date: dateObj ? formatLocalDateParam(dateObj) : (undefined as any),
            companyId: item.companyId,
            holidayId: item.holidayId,
            countryId: item.countryId,
            company: item.company,
            holiday: item.holiday,
            country: item.country,
          });
        }
      });

      if (itemsToUpdate.length === 0) {
        showToast(t('Nothing to update'), 'info');
        return;
      }

      const { payload } = await HolidaysApi.updateHolidaysConfig({
        companyId: company?.id,
        holidayConfigInputList: itemsToUpdate,
      });
      tableDataGenerator(payload.countriesCount, payload.holidayConfigInputList);
      showToast(t('Saved successfully'), 'success');
    } catch (e) {
      showToast(t('Save failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHolidays = async () => {
    const companyId = company?.id;
    if (holidaysInput.some((x) => isNullOrWhiteSpace(x.name))) {
      setHolidayInputError(t('Holiday name is mandatory'));
      return;
    }
    setHolidayInputError(undefined);
    setLoading(true);
    try {
      await HolidaysApi.createHolidays({ companyId, holidayInputList: holidaysInput });
      setCreateHolidayDialogOpen(false);
      setHolidaysInput([
        { description: '', name: '', defaultDate: formatLocalDateParam(new Date()), dateParsed: new Date(), id: 0 },
      ]);
      fetchData();
      showToast(t('Holiday(s) created'), 'success');
    } catch (error: any) {
      if (error.errors && error.errors.length > 0) {
        const msg = error.errors
          .map((e: any) => e.description)
          .filter(Boolean)
          .join(', ');
        showToast(msg || t('Creation failed'), 'error');
      } else {
        showToast(t('Creation failed'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const groupedData = useMemo(
    () =>
      groups.map((g) => ({
        group: g,
        items: items.filter((i) => i.holidayId.toString() === g.key),
      })),
    [groups, items]
  );

  return (
    <div className="relative">
      {/* Loading bar */}
      {loading && (
        <div className="absolute top-0 inset-x-0 z-10 h-1 overflow-hidden rounded bg-muted">
          <div className="h-full w-1/3 animate-[progress_1.5s_ease-in-out_infinite] bg-primary" />
        </div>
      )}

      {/* Create Holiday Dialog */}
      <Dialog open={createHolidayDialogOpen} onOpenChange={setCreateHolidayDialogOpen}>
        <DialogContent size="lg">
          <DialogHeader className="flex items-center justify-between">
            <DialogTitle>{t('Add Holidays')}</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              iconName="PlusCircle"
              title={String(t('Add another row'))}
              onClick={() =>
                setHolidaysInput((prev) => [
                  ...prev,
                  { description: '', name: '', defaultDate: formatLocalDateParam(new Date()), dateParsed: new Date(), id: 0 },
                ])
              }
            >
              {t('Add another row')}
            </Button>
          </DialogHeader>
          <DialogBody className="max-h-[360px] overflow-y-auto space-y-4">
            {holidayInputError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {holidayInputError}
              </div>
            )}
            {holidaysInput.map((x, idx) => (
              <div key={idx} className="relative rounded-lg border border-border p-4 space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <Input
                    label={t('Name') || 'Name'}
                    value={x.name}
                    required
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setHolidaysInput((prev) =>
                        prev.map((it, i) => (i === idx ? { ...it, name: e.target.value } : it))
                      )
                    }
                  />
                  <CustomDatePicker
                    label={t('Date') || 'Date'}
                    value={x.dateParsed}
                    onChange={(newDate: Date | null) => {
                      if (!newDate) return;
                      setHolidaysInput((prev) =>
                        prev.map((it, i) => {
                          if (i !== idx) return it;
                          const dateObj = new Date(newDate);
                          dateObj.setHours(dateObj.getHours() - dateObj.getTimezoneOffset() / 60);
                          return { ...it, dateParsed: newDate, defaultDate: formatLocalDateParam(dateObj) };
                        })
                      );
                    }}
                    size="small"
                    required
                    fullWidth
                  />
                </div>
                <Input
                  label={t('Description') || 'Description'}
                  value={x.description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setHolidaysInput((prev) =>
                      prev.map((it, i) => (i === idx ? { ...it, description: e.target.value } : it))
                    )
                  }
                />
                {idx !== 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 end-2"
                    title={String(t('Remove'))}
                    onClick={() => setHolidaysInput((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <AppIcon name="X" size={16} className="text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateHolidayDialogOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={!loading ? handleCreateHolidays : undefined}
              disabled={loading}
              iconName={loading ? undefined : 'Save'}
              loading={loading}
            >
              {t('Submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top Bar */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <h2 className="flex-1 text-lg font-semibold text-foreground">
          {t('Holidays Configuration')}
        </h2>
        <Button variant="outline" iconName="Plus" onClick={() => setCreateHolidayDialogOpen(true)}>
          {t('Add Holidays')}
        </Button>
        <Button
          disabled={loading}
          iconName={loading ? undefined : 'Save'}
          loading={loading}
          onClick={handleSubmit}
        >
          {t('Submit')}
        </Button>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-lg border border-border bg-card overflow-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
        <Table stickyHeader>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]" />
              <TableHead className="w-[60px]" />
              <TableHead className="min-w-[180px] font-semibold">{t('Country')}</TableHead>
              <TableHead className="min-w-[200px] font-semibold">{t('Date')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && groupedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className="flex flex-col items-center gap-3 py-10">
                    <span className="text-muted-foreground">{t('No holidays configured yet')}</span>
                    <Button variant="outline" iconName="Plus" onClick={() => setCreateHolidayDialogOpen(true)}>
                      {t('Add Holidays')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {groupedData.map((groupData) => (
              <GroupRow
                key={groupData.group.key}
                groupData={groupData}
                onCheckboxChange={(item, checked) => {
                  setItems((prev) =>
                    prev.map((it) =>
                      it.holidayId === item.holidayId && it.countryId === item.countryId ? { ...it, enabled: checked } : it
                    )
                  );
                }}
                onDateChange={(item, newDate) => {
                  if (!newDate) return;
                  setItems((prev) =>
                    prev.map((it) =>
                      it.holidayId === item.holidayId && it.countryId === item.countryId ? { ...it, date: newDate as any } : it
                    )
                  );
                }}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

/* ── Collapsible Group Row ── */
const GroupRow: React.FC<{
  groupData: { group: any; items: HolidayConfigView[] };
  onCheckboxChange: (item: HolidayConfigView, checked: boolean) => void;
  onDateChange: (item: HolidayConfigView, newDate: Date | null) => void;
}> = ({ groupData, onCheckboxChange, onDateChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} asChild>
      <>
        {/* Group header row */}
        <TableRow className="bg-muted/30">
          <TableCell className="w-[50px]">
            <Collapsible.Trigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="expand row">
                <AppIcon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} />
              </Button>
            </Collapsible.Trigger>
          </TableCell>
          <TableCell colSpan={3}>
            <span className="text-sm font-semibold tracking-wide text-foreground">
              {groupData.group.name}
            </span>
          </TableCell>
        </TableRow>

        {/* Collapsible child rows */}
        {open &&
          groupData.items.map((item) => (
            <TableRow key={`${item.holidayId}-${item.countryId}`}>
              <TableCell />
              <TableCell>
                <Checkbox
                  checked={item.enabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCheckboxChange(item, e.target.checked)}
                  aria-label="enable holiday"
                />
              </TableCell>
              <TableCell>
                <span className="text-sm text-foreground">{item.country.name}</span>
              </TableCell>
              <TableCell>
                <CustomDatePicker
                  label="Date"
                  value={new Date(item.date)}
                  onChange={(newDate: Date | null) => onDateChange(item, newDate)}
                  size="small"
                  fullWidth
                />
              </TableCell>
            </TableRow>
          ))}
      </>
    </Collapsible.Root>
  );
};

export default HolidaysConfig;
